import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { notificationService } from '../services/notificationService';

export const useRealtimeNotifications = (userId) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      const [notificationsData, unreadCountData] = await Promise.all([
        notificationService?.getAll(userId),
        notificationService?.getUnreadCount(userId)
      ]);

      setNotifications(notificationsData);
      setUnreadCount(unreadCountData);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err?.message || 'Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService?.markAsRead(notificationId);
      await fetchNotifications();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, [fetchNotifications]);

  const markAllAsRead = useCallback(async () => {
    if (!userId) return;

    try {
      await notificationService?.markAllAsRead(userId);
      await fetchNotifications();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [userId, fetchNotifications]);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationService?.deleteNotification(notificationId);
      await fetchNotifications();
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  }, [fetchNotifications]);

  useEffect(() => {
    if (!userId) return;

    fetchNotifications();

    const channel = supabase
      ?.channel('notifications_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('Notification change detected:', payload);
          setIsLive(true);
          fetchNotifications();
          
          setTimeout(() => setIsLive(false), 2000);
        }
      )
      ?.subscribe((status) => {
        console.log('Notification subscription status:', status);
      });

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [userId, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    isLive,
    lastUpdate,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch: fetchNotifications
  };
};
